// src/api/support.ts

// ---- Типы (как ожидет виджет) ----
export type SupportTicket = {
  id: number;
  subject: string | null;
  status: 'open' | 'pending' | 'resolved' | 'closed';
  category?: string | null;
  last_message_at?: string | null;
  unread_for_user?: boolean;
  unread_for_agent?: boolean;
  date_created?: string;
  date_updated?: string;
};

export type SupportMessage = {
  id: number;
  author_type: 'user' | 'agent';
  author_user_id?: number | null;
  author_agent_id?: number | null;
  body: string;
  attachments?: any;
  date_created: string;
};

// ---- Константы (реальные URL вебхуков) ----
// МАППИНГ:
// CREATE      → e8126bed-a029-4067-b663-17ad333f7ae3
// LIST TICKETS→ 68c306e9-6059-4973-bcd0-cb6aa39795cb
// MSG_LIST    → c330055c-318c-4b1c-8e95-070e9c66fba3
// MSG_SEND    → 7e80fb4f-4fe1-4661-9ea4-07ed03240bb6
const SUPPORT_API = {
  CREATE:  'https://api.24mira.ru/flows/trigger/e8126bed-a029-4067-b663-17ad333f7ae3',
  LIST:    'https://api.24mira.ru/flows/trigger/68c306e9-6059-4973-bcd0-cb6aa39795cb',
  MSG_LIST:'https://api.24mira.ru/flows/trigger/c330055c-318c-4b1c-8e95-070e9c66fba3',
  MSG_SEND:'https://api.24mira.ru/flows/trigger/7e80fb4f-4fe1-4661-9ea4-07ed03240bb6',
};

// Если во флоу проверяешь заголовок X-Flow-Token — положи сюда тот же секрет (или оставь пустым)
const FLOW_TOKEN: string | undefined = undefined;

// ---- Внутренние помощники ----
function getClientId(): string {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem('clientId') || '';
}

function baseHeaders(): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (FLOW_TOKEN) h['X-Flow-Token'] = FLOW_TOKEN;
  return h;
}

async function fetchJSON<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: baseHeaders(),
    body: JSON.stringify(body),
    credentials: 'omit',
    mode: 'cors',
  });

  // пытаемся распарсить JSON даже при ошибке
  const data: any = await res.json().catch(() => ({}));

  // забираем retry-after из тела/заголовка
  const retryAfterHeader = Number(res.headers.get('retry-after'));
  const retry_after =
    data?.retry_after ??
    data?.error?.retry_after ??
    (Number.isFinite(retryAfterHeader) ? retryAfterHeader : null);

  // Directus иногда шлёт 200 с { error: {...} } — считаем это ошибкой
  const hasErrorObject = !!data?.error;

  if (!res.ok || data?.ok === false || hasErrorObject) {
    const err: any = new Error(
      data?.error?.message || data?.message || `HTTP ${res.status}`
    );
    err.status = res.status || 400;
    err.code = data?.error?.code || data?.code || (res.status === 429 ? 'cooldown' : 'unknown');
    err.retry_after = retry_after;
    throw err;
  }

  return data as T;
}

// ---- Публичное API ----

// Реальный userId на клиенте не нужен — всё валидируется во флоу по client_id.
// Оставляем сигнатуру для совместимости с виджетом.
export async function ensureUserId(_clientId: string): Promise<number> {
  if (!_clientId) throw new Error('clientId is empty');
  return 1;
}

// Создать тикет (если хочешь слать первое сообщение здесь — добавь body в payload и во флоу)
export async function createTicket(params: {
  userId?: number;
  subject: string;
  category?: string | null;
  source?: string | null;
  course_id?: number | null;
  body?: string;
}): Promise<SupportTicket> {
  const client_id = getClientId();
  if (!client_id) throw new Error('No client_id');

  const payload = {
    client_id,
    subject: params.subject,
    category: params.category ?? 'other',
    body: (params.body ?? '').trim(), // сейчас не обязателен
  };

  const data = await fetchJSON<{ ok: true; ticket_id: number }>(SUPPORT_API.CREATE, payload);
  const id = Number(data.ticket_id);

  return {
    id,
    subject: params.subject,
    status: 'open',
    category: params.category ?? 'other',
    unread_for_user: false,
    unread_for_agent: true,
    last_message_at: new Date().toISOString(),
  };
}

// Список тикетов для текущего client_id (нормализуем id)
export async function listTickets(_userId?: number): Promise<SupportTicket[]> {
  const client_id = getClientId();
  if (!client_id) return [];

  const data = await fetchJSON<{ ok: true; tickets: any[] }>(SUPPORT_API.LIST, { client_id });
  const raw = data.tickets ?? [];

  const normalized: SupportTicket[] = raw
    .map((t: any) => {
      const id =
        t.id ??
        t.ID ??
        t.Id ??
        t.integer ??
        t.Integer ??
        null;

      if (!Number.isFinite(Number(id))) return null;

      return {
        id: Number(id),
        subject: t.subject ?? null,
        status: (t.status as any) ?? 'open',
        category: t.category ?? null,
        last_message_at: t.last_message_at
          ? (String(t.last_message_at).endsWith('Z')
              ? String(t.last_message_at)
              : new Date(t.last_message_at).toISOString())
          : null,
        unread_for_user: Boolean(t.unread_for_user),
        unread_for_agent: Boolean(t.unread_for_agent),
        date_created: t.date_created ?? undefined,
        date_updated: t.date_updated ?? undefined,
      } as SupportTicket;
    })
    .filter(Boolean) as SupportTicket[];

  return normalized;
}

// Список сообщений по тикету (сервер сам может сбрасывать unread_for_user=false)
export async function listMessages(ticketId: number): Promise<SupportMessage[]> {
  const client_id = getClientId();
  const id = Number(ticketId);
  if (!client_id || !Number.isFinite(id)) {
    console.warn('listMessages: skip, bad params', { client_id, ticketId });
    throw new Error('listMessages: missing client_id or ticketId');
  }

  const payload = { client_id, ticket_id: id };
  console.debug('MSG_LIST →', SUPPORT_API.MSG_LIST, payload);

  const data = await fetchJSON<{ ok: true; messages: SupportMessage[] }>(
    SUPPORT_API.MSG_LIST,
    payload
  );
  return data.messages ?? [];
}

// Отправить сообщение в тикет
export async function postMessage(params: { ticketId: number; userId?: number; body: string }): Promise<SupportMessage> {
  const client_id = getClientId();
  const id = Number(params.ticketId);
  const body = (params.body || '').trim();
  if (!client_id || !Number.isFinite(id) || !body) {
    throw new Error('postMessage: invalid params');
  }

  // пусть helper обработает 429/ok:false/{error:{...}} и бросит расширенную ошибку
  const data = await fetchJSON<{ ok: true; message?: SupportMessage }>(
    SUPPORT_API.MSG_SEND,
    { client_id, ticket_id: id, body }
  );

  // если флоу вернул собранный объект сообщения — используем его
  if (data?.message) {
    return data.message as SupportMessage;
  }

  // fallback: «эхо»-сообщение для мгновенной отрисовки
  return {
    id: Date.now(),
    author_type: 'user',
    body,
    date_created: new Date().toISOString(),
    author_user_id: undefined,
    author_agent_id: null,
  };
}

// Флаг прочитанности сервер сбрасывает сам
export async function markTicketReadForUser(_ticketId: number): Promise<void> {
  return;
}
